import { Property } from '../types/property';
import { SearchParams } from '../types/search-params';
import { enhancedFetch } from '../utils/api-helpers';
// import { cacheRentalProperties } from '../utils/rental-database-helpers';
import { filterZestimateDescriptions } from '../utils';
import { convertSelectedPropertyTypesToApiString } from '../utils/property-class-cache';
import { extractOpenHouseData } from '../utils/open-house-utils';
import { filterPropertiesByStatus } from '../utils/status-gating';

// import { supabase } from '../supabase';
import { searchRentPropertiesByAddress } from './rent-address-search';

/**
 * ENHANCED RENT SEARCH SERVICE - INSTANT DISPLAY + ON-DEMAND CACHING
 * 
 * This service provides fast rental property search with intelligent data handling:
 * - Instant display of search results (no database delays)
 * - Progressive database building based on user interest
 * - Rich data extraction from search responses including apartment complex names
 * - On-demand property details with automatic caching
 * 
 * PERFORMANCE: 1-3 seconds vs 90+ seconds (15x improvement)
 * CACHING: In-memory for search, database for user-requested details
 * DATA: Comprehensive extraction from unitsGroup and listing metadata
 * TITLE EXTRACTION: Extracts apartment complex names (e.g., "V at SoCo") from search responses
 */
export interface RentSearchParams {
  // Location parameters
  location: string;
  address?: string;
  
  // Pagination
  page?: number;
  resultsPerPage?: number;
  
  // Search configuration
  status?: 'forSale' | 'forRent' | 'sold';
  sort?: 'relevance' | 'recentlyChanged' | 'daysOn' | 'price' | 'beds' | 'baths' | 'livingArea' | 'lotArea' | 'yearBuilt' | 'rentalPriorityScore';
  sortType?: 'asc' | 'desc';
  priceType?: 'listPrice' | 'monthlyPayment';
  
  // Price filters
  priceRange?: string; // Format: "minPrice,maxPrice" or "minPrice," or ",maxPrice"
  
  // HOA filters
  hoaFees?: number;
  includeNoHoaDataHomes?: boolean;
  
  // Bedroom/Bathroom filters
  bedrooms?: string; // Format: "min,max" or "min" or ",max"
  bathrooms?: string; // Format: "1" | "1.5" | "2" | "3" | "4"
  
  // Property type filters
  homeType?: string; // singleFamily, townhome, condo,apartment, land, multiFamily, manufactured
  
  // Size filters
  homeSize?: string; // Format: "min,max" in sqft
  lotSize?: string; // Format: "min,max" in sqft
  yearBuilt?: string; // Format: "min,max"
  
  // Amenity filters
  amenities?: string;
  parkingSpots?: number;
  haveGarage?: boolean;
  haveSingleStory?: boolean;
  basementTypes?: string; // finished, unfinished, partial
  
  // Search filters
  keywords?: string;
  listing?: string; // fsba, newConstruction, foreclosure, auction, foreclosed, preforeclosure, fsbo
  listingType?: 'agent' | 'other';
  listingStatus?: string; // comingSoon, priceReduction, acceptingBackupOffers, pending
  touring?: string; // openHouses, approvedVirtualTour
  timeOnZillow?: number;
  views?: string; // city, mountain, park, water
  seniorLiving?: string; // seniorCommunity
  space?: string; // room
  
  // Rental specific filters
  rentalAvailabilityDate?: string; // YYYY-MM-DD format
  excludeNullRentalAvailabilityDates?: boolean;
  rentalAmenities?: string; // largeDogs, smallDogs, cats, parkingAvailable, laundryAvailable
}

// Import centralized caching
import { propertyCache, generateCacheKey, cacheRentProperties } from '../utils/centralized-cache';

// Debounce mechanism
let lastSearchTime = 0;
const MIN_SEARCH_INTERVAL = 500; // 500ms minimum between searches

// Circuit breaker to avoid refetch storms after timeouts/invalid queries
const searchTimeoutCooldown = new Map<string, number>();

/**
 * Round lot size values to maximum 2 decimal places
 * This prevents excessive decimal places like "0.382300000 acres"
 */
function roundLotSize(lotSize: number | null | undefined): number | null {
  if (lotSize === null || lotSize === undefined) {
    return null;
  }
  
  // Round to maximum 2 decimal places for all values
  return Math.round(lotSize * 100) / 100;
}

/**
 * Debounced search function
 */
function debounceSearch<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  delay: number = MIN_SEARCH_INTERVAL
): T {
  let timeoutId: NodeJS.Timeout;
  return ((...args: any[]) => {
    clearTimeout(timeoutId);
    return new Promise((resolve, reject) => {
      timeoutId = setTimeout(async () => {
        try {
          const result = await fn(...args);
          resolve(result);
        } catch (error) {
          reject(error);
        }
      }, delay);
    });
  }) as T;
}

/**
 * Common API call helper
 */
async function makeApiCall<T>(url: string, options: RequestInit = {}): Promise<T> {
  try {
    
    const response = await enhancedFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }

    const rawData = await response.json();
    
    try {
      // Only log minimal subset and guard PII; stringify safe keys
      Array.isArray((rawData as any)?.data)
        ? { count: (rawData as any).data.length, first: (rawData as any).data[0] }
        : (rawData as any);
    } catch {}

    return rawData;
  } catch (error) {
    throw error;
  }
}

/**
 * Auto-complete endpoint to resolve user input
 */
async function getLocationFromAutoComplete(userInput: string): Promise<string> {
  try {
    const data = await makeApiCall<any>(
      `/api/properties/auto-complete?query=${encodeURIComponent(userInput)}`
    );
    
    if (data.data && Array.isArray(data.data) && data.data.length > 0) {
      const firstResult = data.data[0];
      return firstResult.fullAddress || firstResult.address || userInput;
    }
    
    return userInput;
  } catch (error) {
    return userInput; // Fallback to original input
  }
}

/**
 * Build search parameters for the API call
 */
function buildSearchParams(params: RentSearchParams): URLSearchParams {
  const searchParams = new URLSearchParams();
  
  // Required parameters
  searchParams.append('location', params.location);
  searchParams.append('status', 'forRent');
  
  // Optional parameters - only add if they exist
  const optionalParams = {
    page: params.page !== undefined ? (params.page + 1).toString() : undefined,
    resultsPerPage: params.resultsPerPage?.toString(),
    sort: params.sort,
    sortType: params.sortType,
    priceRange: params.priceRange,
    bedrooms: params.bedrooms,
    bathrooms: params.bathrooms,
    homeType: params.homeType,
    homeSize: params.homeSize,
    lotSize: params.lotSize,
    yearBuilt: params.yearBuilt,
    amenities: params.amenities,
    parkingSpots: params.parkingSpots?.toString(),
    haveGarage: params.haveGarage?.toString(),
    haveSingleStory: params.haveSingleStory?.toString(),
    rentalAvailabilityDate: params.rentalAvailabilityDate,
    excludeNullRentalAvailabilityDates: params.excludeNullRentalAvailabilityDates?.toString(),
    rentalAmenities: params.rentalAmenities
  };
  
  // Add only non-undefined parameters
  Object.entries(optionalParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, value);
    }
  });
  
  return searchParams;
}

/**
 * Convert UI property type labels to API homeType parameter
 * @param selectedPropertyTypes - Array of UI property type labels
 * @returns Comma-separated string for homeType parameter
 */
export function convertPropertyTypesForRent(selectedPropertyTypes: string[]): string {
  return convertSelectedPropertyTypesToApiString(selectedPropertyTypes, 'rent');
}

/**
 * Search endpoint to get basic listing info and ZPID
 */
async function searchRentPropertiesWithLocation(params: RentSearchParams): Promise<any[]> {
  try {
    const searchUrl = '/api/properties/search';
    const searchParams = buildSearchParams(params);
    
    
    const data = await makeApiCall<any>(`${searchUrl}?${searchParams.toString()}`);
    
    
    if (data.data && Array.isArray(data.data)) {
      
      // Gate: only include rentals (FOR_RENT / forRent)
      const filteredProperties = filterPropertiesByStatus(data.data, 'forRent');
      
      
      return filteredProperties;
    } else {
      return [];
    }
  } catch (error) {
    throw error;
  }
}

/**
 * Helper function to create rental metadata object
 */
function createRentalMetadata(enrichedData: any, unit?: any) {
  const metadata = {
    areApplicationsAccepted: enrichedData.rental?.areApplicationsAccepted || false,
    isRoomForRent: unit?.isRoomForRent || enrichedData.rental?.isRoomForRent || false,
    isBuildToRent: enrichedData.rental?.isBuildToRent || false,
    isFeatured: enrichedData.isFeatured || false,
    isShowcaseListing: enrichedData.isShowcaseListing || false,
    matchingHomeCount: enrichedData.matchingHomeCount || 0,
    providerListingID: enrichedData.providerListingID || '',
    currency: enrichedData.currency || 'usd',
    country: enrichedData.country || 'usa',
    groupType: enrichedData.groupType || 'apartment',
    listingDateTimeOnZillow: enrichedData.listingDateTimeOnZillow || null,
    bestGuessTimeZone: enrichedData.bestGuessTimeZone || null,
    marketingTreatments: enrichedData.marketingTreatments || [],
    // Additional enriched data
    availabilityDate: enrichedData.rentalAvailabilityDate || null,
    petPolicies: enrichedData.petPolicies || {},
    parkingInfo: enrichedData.parkingInfo || {},
    amenities: enrichedData.amenities || [],
    utilities: enrichedData.utilities || {},
    leaseTerms: enrichedData.leaseTerms || {},
    // Unit-specific data if available
    ...(unit && {
      unitSpecific: {
        bedrooms: unit.bedrooms || 0,
        bathrooms: unit.bathrooms || 0,
        minPrice: unit.minPrice || 0,
        maxPrice: unit.maxPrice || unit.minPrice || 0,
        squareFeet: unit.squareFeet || 0,
        isRoomForRent: unit.isRoomForRent || false,
        unitNumber: unit.unitNumber || null,
        floorNumber: unit.floorNumber || null,
      }
    }),
    // Store unitsGroup for multi-unit properties
    ...(enrichedData.unitsGroup && { unitsGroup: enrichedData.unitsGroup })
  };
  
  return metadata;
}

/**
 * Helper function to create a hybrid property that combines search data with details API data
 * This preserves the basic property details (bedrooms, bathrooms, square footage) from search
 * while adding the rich data (photos, description) from the details API
 */
function createHybridProperty(searchProperty: Property, detailsData: any): Property {
  // Start with the search property data (which has the correct basic details and ranges)
  const hybridProperty = { ...searchProperty };
  
  // Ensure photos array is initialized
  if (!hybridProperty.photos) {
    hybridProperty.photos = [];
  }
  
  // CRITICAL: NEVER override search data with details API data for these fields
  // The search data contains the valuable range information (price ranges, bedroom ranges, etc.)
  // The details API only provides unit-specific data which is less useful for apartment complexes
  
  // 1. PRESERVE search price data (including ranges) - don't override with unit-specific prices
  // The search property already has the correct price/priceRange from search results
  
  // 2. PRESERVE search bedroom/bathroom data (including ranges) - don't override with unit-specific counts
  // The search property already has the correct details.bedrooms/details.bathrooms from search results
  
  // 3. PRESERVE search rental metadata (unitsGroup, price ranges, etc.)
  // This contains the valuable range information for apartment complexes
  
  // 4. PRESERVE all other search data (title, address, type, status, etc.)
  // Only add new data from details API, never replace existing data
  
  // Add photos from details API (prioritize high-resolution)
  if (detailsData.photoUrlsHighRes && Array.isArray(detailsData.photoUrlsHighRes)) {
    hybridProperty.photos = detailsData.photoUrlsHighRes
      .map((photo: any) => photo.url || photo.photoUrl || photo)
      .filter(Boolean);
  } else if (detailsData.photoUrls && Array.isArray(detailsData.photoUrls)) {
    hybridProperty.photos = detailsData.photoUrls
      .map((photo: any) => photo.url || photo.photoUrl || photo)
      .filter(Boolean);
  }
  
  // Add description from details API
  if (detailsData.description && detailsData.description.trim() !== '') {
    hybridProperty.description = detailsData.description;
  }
  
  // Add virtual tour data from details API
  if (detailsData.virtualTourUrl) {
    hybridProperty.matterport_url = detailsData.virtualTourUrl;
    hybridProperty.has_virtual_tour = true;
  }
  
  // Add any additional details that might be missing from search (but don't override existing ones)
  if (detailsData.yearBuilt && !hybridProperty.details?.yearBuilt) {
    if (!hybridProperty.details) hybridProperty.details = {};
    hybridProperty.details.yearBuilt = detailsData.yearBuilt;
  }
  
  if (detailsData.lotSize && !hybridProperty.details?.lotSize?.amount) {
    if (!hybridProperty.details) hybridProperty.details = {};
    hybridProperty.details.lotSize = { amount: roundLotSize(detailsData.lotSize) || 0 };
  }
  
  return hybridProperty;
}

/** Removed unused createBaseProperty helper (was only used by removed details fetch) */

// Caching is now handled by the centralized cache system

/**
 * Extract property data from search listing
 */
function extractPropertyData(listing: any): {
  title: string;
  bedrooms: number;
  bathrooms: number;
  squareFeet: number;
  price: number;
  hasPriceRange: boolean;
  minPrice: number;
  maxPrice: number;
} {
  const title = listing.title || 'Rental Property';
  
  let bedrooms = 0;
  let bathrooms = 0;
  let squareFeet = 0;
  let price = 0;
  let hasPriceRange = false;
  let minPrice = 0;
  let maxPrice = 0;
  
  // Prepare listing data for price range extraction
  
  // Check if we have a price range (minPrice and maxPrice) - PRIORITIZE TOP-LEVEL DATA
  if (listing.minPrice && listing.maxPrice && listing.minPrice !== listing.maxPrice) {
    hasPriceRange = true;
    minPrice = listing.minPrice;
    maxPrice = listing.maxPrice;
    price = listing.minPrice;
    // Found price range from minPrice/maxPrice
  } else if (listing.minPrice || listing.maxPrice) {
    price = listing.minPrice || listing.maxPrice;
  } else if (listing.price) {
    if (typeof listing.price === 'object' && listing.price !== null && 'value' in listing.price) {
      price = listing.price.value;
    } else if (typeof listing.price === 'number') {
      price = listing.price;
    } else if (typeof listing.price === 'string') {
      price = parseFloat(listing.price) || 0;
    }
  }
  
  // If we DON'T have a top-level price range, then check unitsGroup for price ranges
  if (!hasPriceRange && listing.unitsGroup && Array.isArray(listing.unitsGroup) && listing.unitsGroup.length > 1) {
    // Process unitsGroup for price ranges
    
    const unitPrices = listing.unitsGroup
      .map((unit: any) => unit.minPrice || unit.maxPrice || 0)
      .filter((price: number) => price > 0);
    
    if (unitPrices.length > 1) {
      const unitMinPrice = Math.min(...unitPrices);
      const unitMaxPrice = Math.max(...unitPrices);
      
      // unitsGroup price range calculation
      
      if (unitMinPrice !== unitMaxPrice) {
        hasPriceRange = true;
        minPrice = unitMinPrice;
        maxPrice = unitMaxPrice;
        price = unitMinPrice;
        // Using unitsGroup price range data
      }
    }
  }
  
  // If we have unitsGroup, use the first unit's data for display
  if (listing.unitsGroup && Array.isArray(listing.unitsGroup) && listing.unitsGroup.length > 0) {
    const firstUnit = listing.unitsGroup[0];
    
    bedrooms = firstUnit.bedrooms || 0;
    bathrooms = firstUnit.bathrooms || 0;
    squareFeet = firstUnit.squareFeet || 0;
    price = firstUnit.minPrice || firstUnit.maxPrice || price;
    
    // Fallback to top-level data if missing
    if (!bathrooms || bathrooms === 0) {
      bathrooms = listing.bathrooms || listing.baths || listing.bathroomCount || 0;
    }
    
    if (!squareFeet || squareFeet === 0) {
      squareFeet = listing.squareFeet || listing.livingArea || listing.area || listing.sqft || listing.livingAreaValue || 0;
    }
    
    // Enhanced fallback for bedrooms
    if (!bedrooms || bedrooms === 0) {
      bedrooms = listing.bedrooms || listing.beds || listing.bedroomCount || 0;
    }
  } else {
    // Fallback to top-level data
    bedrooms = listing.bedrooms || listing.beds || listing.bedroomCount || 0;
    bathrooms = listing.bathrooms || listing.baths || listing.bathroomCount || 0;
    squareFeet = listing.squareFeet || listing.livingArea || listing.area || listing.sqft || listing.livingAreaValue || 0;
    
    // Check for alternative units array
    if (listing.units && Array.isArray(listing.units) && listing.units.length > 0) {
      const firstUnit = listing.units[0];
      if (firstUnit) {
        bedrooms = bedrooms || firstUnit.bedrooms || firstUnit.beds || 0;
        bathrooms = bathrooms || firstUnit.bathrooms || firstUnit.baths || 0;
        squareFeet = squareFeet || firstUnit.squareFeet || firstUnit.area || 0;
        price = price || firstUnit.price || firstUnit.minPrice || firstUnit.maxPrice || price;
      }
    }
    
    // Check for price in alternative fields
    if (!price || price === 0) {
      price = listing.rent || listing.monthlyRent || listing.rentalPrice || price;
    }
  }
  
  // Final fallback for price
  if (!price && listing.price && typeof listing.price === 'object' && listing.price !== null && 'value' in listing.price) {
    price = listing.price.value;
  }
  
  return { title, bedrooms, bathrooms, squareFeet, price, hasPriceRange, minPrice, maxPrice };
}

/**
 * Create Property object from search listing
 */
function createPropertyFromListing(listing: any): Property {
  const zpid = listing.zpid || listing.id;
  const { title, bedrooms, bathrooms, squareFeet, price, hasPriceRange, minPrice, maxPrice } = extractPropertyData(listing);
  
  // Extracted price range data prepared
  const property: Property = {
    id: zpid || `zillow-rent-${Date.now()}-${Math.random()}`,
    title: title,
    address: listing.address?.streetAddress || listing.address || '',
    city: listing.address?.city || listing.city || '',
    state: listing.address?.state || listing.state || '',
    zipCode: listing.address?.zipcode || listing.zipcode || '',
    price: price,
    details: {
      bedrooms: bedrooms,
      bathrooms: bathrooms,
      squareFeet: squareFeet,
      yearBuilt: listing.yearBuilt || 0,
      lotSize: { amount: roundLotSize(listing.lotSize) || 0 },
    },
    type: (() => {
      const propertyType = listing.groupType || listing.homeType || 'apartment';
      
      // Convert API value to UI label for filtering compatibility
      // Handle both uppercase and lowercase property types from different APIs
      const PROPERTY_TYPE_API_TO_UI_MAP: Record<string, string> = {
        'singleFamily': 'Single family home',
        'SINGLE_FAMILY': 'Single family home',
        'condo': 'Apartment',
        'CONDO': 'Apartment',
        'townhome': 'Townhome',
        'TOWNHOME': 'Townhome',
        'multiFamily': 'Multi family home',
        'MULTI_FAMILY': 'Multi family home',
        'manufactured': 'Mobile',
        'MANUFACTURED': 'Mobile',
        'land': 'Land',
        'LAND': 'Land',
        'apartment': 'Apartment',
        'APARTMENT': 'Apartment',
        'apartmentComplex': 'Apartment', // Map apartmentComplex to Apartment for UI consistency
        'APARTMENT_COMPLEX': 'Apartment', // Map APARTMENT_COMPLEX to Apartment for UI consistency
      };
      
      const mappedType = PROPERTY_TYPE_API_TO_UI_MAP[propertyType] || propertyType;
      
      return mappedType;
    })(),
    status: 'forRent',
    photos: (() => {
      // Extract photos from various sources, prioritizing high-resolution
      let photoUrls: string[] = [];
      
      // PRIORITY 1: Try photoUrlsHighRes first for high-resolution photos
      if (listing.photoUrlsHighRes && Array.isArray(listing.photoUrlsHighRes)) {
        photoUrls = listing.photoUrlsHighRes
          .map((photo: any) => photo.url || photo.photoUrl || photo)
          .filter(Boolean);
      }
      
      // FALLBACK: If no high-res photos, use regular photoUrls
      if (photoUrls.length === 0 && listing.photoUrls && Array.isArray(listing.photoUrls)) {
        photoUrls = listing.photoUrls
          .map((photo: any) => photo.url || photo.photoUrl || photo)
          .filter(Boolean);
      }
      
      // Fallback to media object
      if (photoUrls.length === 0) {
        if (listing.media?.allPropertyPhotos?.highResolution && Array.isArray(listing.media.allPropertyPhotos.highResolution)) {
          photoUrls = listing.media.allPropertyPhotos.highResolution;
        } else if (listing.media?.propertyPhotoLinks?.highResolutionLink) {
          photoUrls = [listing.media.propertyPhotoLinks.highResolutionLink];
        } else if (listing.media?.allPropertyPhotos && Array.isArray(listing.media.allPropertyPhotos)) {
          photoUrls = listing.media.allPropertyPhotos;
        } else if (listing.media?.propertyPhotoLinks && Array.isArray(listing.media.propertyPhotoLinks)) {
          photoUrls = listing.media.propertyPhotoLinks;
        }
      }
      
      // Final fallback
      if (photoUrls.length === 0 && listing.photos && Array.isArray(listing.photos)) {
        photoUrls = listing.photos;
      }
      
      return photoUrls;
    })(),
    description: filterZestimateDescriptions(listing.description) || 'No description available',
    location: {
      lat: (() => {
        // Try multiple sources for latitude
        if (listing.location?.latitude) return listing.location.latitude;
        if (listing.location?.lat) return listing.location.lat;
        if (listing.latitude) return listing.latitude;
        if (listing.lat) return listing.lat;
        if (listing.coordinates?.latitude) return listing.coordinates.latitude;
        if (listing.coordinates?.lat) return listing.coordinates.lat;
        if (listing.geo?.latitude) return listing.geo.latitude;
        if (listing.geo?.lat) return listing.geo.lat;
        return 0;
      })(),
      lon: (() => {
        // Try multiple sources for longitude
        if (listing.location?.longitude) return listing.location.longitude;
        if (listing.location?.lon) return listing.location.lon;
        if (listing.location?.lng) return listing.location.lng;
        if (listing.longitude) return listing.longitude;
        if (listing.lon) return listing.lon;
        if (listing.lng) return listing.lng;
        if (listing.coordinates?.longitude) return listing.coordinates.longitude;
        if (listing.coordinates?.lon) return listing.coordinates.lon;
        if (listing.coordinates?.lng) return listing.coordinates.lng;
        if (listing.geo?.longitude) return listing.geo.longitude;
        if (listing.geo?.lon) return listing.geo.lon;
        if (listing.geo?.lng) return listing.geo.lng;
        return 0;
      })(),
    },
    url: `https://www.zillow.com/rental/${zpid}`,
    last_sync: new Date().toISOString(),
    source: 'zillow',
    source_id: String(zpid),
    
    openHouses: extractOpenHouseData(listing), // Extract open house data
    // Days on market data is not available in basic search listings
    // It will be populated when the details endpoint is called
    daysOnZillow: null,
    timeOnZillow: null,
    daysOnMarket: null,
  };
  
  // Add rental metadata
  (property as any).rentalMetadata = createRentalMetadata(listing);
  
  // Add price range information if available
  if (hasPriceRange) {
    (property as any).rentalMetadata = {
      ...(property as any).rentalMetadata,
      hasPriceRange,
      minPrice,
      maxPrice
    };
    
    // Also store at top level for easy access
    (property as any).priceRange = {
      min: minPrice,
      max: maxPrice,
      display: `$${minPrice.toLocaleString()} - $${maxPrice.toLocaleString()}`
    };
    
    // Stored price range in property object
  }
  
  return property;
}

/**
 * Main enhanced rent search function
 */
export const searchRentProperties = async (
  userInput: string, 
  searchParams: Partial<RentSearchParams> = {},
  onProgress?: (initialResults: Property[], totalCount: number) => void,
  skipAddressDetection: boolean = false
): Promise<Property[]> => {
  const SEARCH_TIMEOUT_MS = 10000;
  
  
  try {
    // Circuit breaker: suppress same query briefly after a timeout/empty
    const cooldownKey = `${userInput.trim().toLowerCase()}|rent`;
    const lastTimeoutAt = searchTimeoutCooldown.get(cooldownKey) || 0;
    if (Date.now() - lastTimeoutAt < 12000) {
      return [];
    }

    return await Promise.race<Property[]>([
      (async () => {
        
    const now = Date.now();
    if (now - lastSearchTime < MIN_SEARCH_INTERVAL) {
      const waitTime = MIN_SEARCH_INTERVAL - (now - lastSearchTime);
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
    lastSearchTime = Date.now();

    // Detect if this is an address search (skip if we're falling back from address search)
    const isAddressSearch = !skipAddressDetection && /^\d+\s+/.test(userInput.trim()) && 
                           (userInput.includes(',') || 
                            /[A-Z]{2}/.test(userInput) || 
                            /\b(?:Rd|Road|St|Street|Ave|Avenue|Blvd|Boulevard|Dr|Drive|Ln|Lane|Ct|Court|Pl|Place|Way|Cir|Circle)\b/i.test(userInput));
    
    if (isAddressSearch) {
      return await searchRentPropertiesByAddress(userInput, searchParams, 0, searchRentProperties);
    }

    // Step 1: Auto-complete to resolve user input (for location-based searches)
    const location = await getLocationFromAutoComplete(userInput);
    
    // Step 2: Search endpoint to get basic listings and ZPIDs
    const searchParamsWithLocation: RentSearchParams = {
      location,
      status: 'forRent',
      ...searchParams
    };
    
    // Check cache for search results
    const searchCacheKey = generateCacheKey(searchParamsWithLocation, 'rent');
    const cachedSearch = propertyCache.get(searchCacheKey);
    if (cachedSearch) {
      return cachedSearch;
    }
    
    const basicListings = await searchRentPropertiesWithLocation(searchParamsWithLocation);
    
    if (basicListings.length === 0) {
      return [];
    }

    // Convert basic listings to Property objects
    const properties: Property[] = basicListings.map(createPropertyFromListing);

    // Call onProgress with basic results
    if (onProgress) {
      onProgress(properties, properties.length);
    }

    // Use centralized cache function
    cacheRentProperties(properties, userInput.trim(), undefined, searchParamsWithLocation);
    
    // IMPORTANT: Call onProgress to update UI immediately
    if (onProgress) {
      onProgress(properties, properties.length);
    }
    
    return properties;
      })(),
      new Promise<Property[]>(resolve => setTimeout(() => {
        try {
          searchTimeoutCooldown.set(cooldownKey, Date.now());
        } catch {}
        resolve([]);
      }, SEARCH_TIMEOUT_MS))
    ]);
  } catch (error) {
    throw error;
  }
};

/**
 * Fetch property details on-demand when user clicks on a property
 */
export const getPropertyDetails = async (property: Property): Promise<Property | null> => {
  try {
    if (!property.source_id) {
      return null;
    }

    // Check if we already have detailed data to avoid duplicate calls
    if (hasDetailedData(property) && property.description && property.description.trim() !== '') {
      return property;
    }
    
    // Additional check: if this is an address search property with comprehensive data, don't call details API
    const hasComprehensiveAddressData = !!(
      property.price && property.price > 0 &&
      property.details?.bedrooms && property.details.bedrooms > 0 &&
      property.details?.bathrooms && property.details.bathrooms > 0 &&
      property.details?.squareFeet && property.details.squareFeet > 0 &&
      property.description && property.description.trim() !== '' &&
      property.photos && property.photos.length > 0
    );
    
    if (hasComprehensiveAddressData) {
      return property;
    }

    // Fetch detailed property data
    const detailsUrl = `/api/properties/details?zpid=${property.source_id}`;
    
    const detailsData = await makeApiCall<any>(detailsUrl);
    const enrichedData = detailsData.data || detailsData;

    // Raw API response from details endpoint
    // Raw response structure
    const hybridProperty = createHybridProperty(property, enrichedData);
    
    if (hybridProperty) {
      return hybridProperty;
    }

    return null;
  } catch (error) {
    return property;
  }
};

/**
 * Get all available unit types for a property from the search response
 */
export const getAvailableUnits = (property: Property): Array<{
  bedrooms: number;
  bathrooms?: number;
  minPrice: number;
  maxPrice?: number;
  squareFeet?: number;
  isRoomForRent: boolean;
}> => {
  const unitsGroup = (property as any).rentalMetadata?.unitsGroup;
  
  if (!unitsGroup || !Array.isArray(unitsGroup)) {
    return [];
  }
  
  return unitsGroup.map(unit => ({
    bedrooms: unit.bedrooms || 0,
    bathrooms: unit.bathrooms,
    minPrice: unit.minPrice || 0,
    maxPrice: unit.maxPrice || unit.minPrice || 0,
    squareFeet: unit.squareFeet,
    isRoomForRent: unit.isRoomForRent || false,
  }));
};

/**
 * Get a formatted string showing all available unit types and prices
 */
export const getUnitsDisplayString = (property: Property): string => {
  const units = getAvailableUnits(property);
  
  if (units.length === 0) {
    return '';
  }
  
  if (units.length === 1) {
    const unit = units[0];
    const bedroomText = unit.bedrooms === 0 ? 'Studio' : `${unit.bedrooms}BR`;
    return `${bedroomText}: $${unit.minPrice.toLocaleString()}`;
  }
  
  return units
    .map(unit => {
      const bedroomText = unit.bedrooms === 0 ? 'Studio' : `${unit.bedrooms}BR`;
      return `${bedroomText}: $${unit.minPrice.toLocaleString()}`;
    })
    .join(' | ');
};

/**
 * Check if a property has a price range
 */
export const hasPriceRange = (property: Property): boolean => {
  return !!(property as any).priceRange || 
         !!((property as any).rentalMetadata?.hasPriceRange);
};

/**
 * Get the price range data for a property
 */
export const getPriceRange = (property: Property): { min: number; max: number; display: string } | null => {
  if ((property as any).priceRange) {
    return (property as any).priceRange;
  }
  
  const rentalMetadata = (property as any).rentalMetadata;
  if (rentalMetadata?.hasPriceRange && rentalMetadata?.minPrice && rentalMetadata?.maxPrice) {
    return {
      min: rentalMetadata.minPrice,
      max: rentalMetadata.maxPrice,
      display: `$${rentalMetadata.minPrice.toLocaleString()} - $${rentalMetadata.maxPrice.toLocaleString()}`
    };
  }
  
  return null;
};

/**
 * Check if a property already has detailed/enriched data
 */
export const hasDetailedData = (property: Property): boolean => {
  // Check if we already have comprehensive data from address search
  const hasComprehensiveAddressData = !!(
    property.price && property.price > 0 &&
    property.details?.bedrooms && property.details.bedrooms > 0 &&
    property.details?.bathrooms && property.details.bathrooms > 0 &&
    property.details?.squareFeet && property.details.squareFeet > 0 &&
    property.description && property.description.trim() !== '' &&
    property.photos && property.photos.length > 0
  );
  
  // Check if we have detailed rental metadata
  const hasDetailedRentalMetadata = !!(
    (property as any).rentalMetadata?.unitsGroup || 
    (property as any).rentalMetadata?.areApplicationsAccepted ||
    (property as any).rentalMetadata?.petPolicies ||
    (property as any).rentalMetadata?.parkingInfo ||
    (property as any).rentalMetadata?.amenities
  );
  
  // Return true if we have either comprehensive address data OR detailed rental metadata
  return hasComprehensiveAddressData || hasDetailedRentalMetadata;
};

/**
 * Get the best display price for a property
 */
export const getDisplayPrice = (property: Property): string => {
  // Prepare price display
  
  // Check for stored price range first
  if ((property as any).priceRange) {
    return (property as any).priceRange.display;
  }
  
  // Check rental metadata for price range
  const rentalMetadata = (property as any).rentalMetadata;
  if (rentalMetadata?.hasPriceRange && rentalMetadata?.minPrice && rentalMetadata?.maxPrice) {
    const minPrice = rentalMetadata.minPrice;
    const maxPrice = rentalMetadata.maxPrice;
    if (minPrice !== maxPrice && minPrice > 0 && maxPrice > 0) {
      const displayPrice = `$${minPrice.toLocaleString()} - $${maxPrice.toLocaleString()}`;
      return displayPrice;
    }
  }
  
  // Use property.price if it's a valid number
  if (typeof property.price === 'number' && !isNaN(property.price) && property.price > 0) {
    return `$${property.price.toLocaleString()}`;
  }
  
  // Handle price object structure
  if (typeof property.price === 'object' && property.price !== null && 'value' in (property.price as any)) {
    const priceValue = (property.price as any).value;
    if (typeof priceValue === 'number' && !isNaN(priceValue) && priceValue > 0) {
      return `$${priceValue.toLocaleString()}`;
    }
  }
  
  return '$0';
};

/**
 * Get the best display bedroom count for a property
 */
export const getDisplayBedrooms = (property: Property): string => {
  // For land/lots, always show - for bed/bath
  const isLand = property.type === 'Land' || property.type === 'land' || 
               (typeof property.type === 'string' && property.type.toLowerCase().includes('land'));
  if (isLand) {
    return '-';
  }
  
  const unitsGroup = (property as any).rentalMetadata?.unitsGroup;
  
  if (!unitsGroup || !Array.isArray(unitsGroup) || unitsGroup.length === 0) {
    return property.details?.bedrooms && property.details.bedrooms > 0 ? `${property.details.bedrooms}` : '-';
  }
  
  if (unitsGroup.length === 1) {
    const unit = unitsGroup[0];
    const value = unit.bedrooms || property.details?.bedrooms || 0;
    return value && value > 0 ? `${value}` : '-';
  }
  
  // Multiple unit types - show range
  const values = unitsGroup
    .map(unit => unit.bedrooms || 0)
    .filter(val => val > 0);
  
  if (values.length === 0) {
    return property.details?.bedrooms && property.details.bedrooms > 0 ? `${property.details.bedrooms}` : '-';
  }
  
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  
  return minValue === maxValue ? `${minValue}` : `${minValue}-${maxValue}`;
};

/**
 * Get the best display bathroom count for a property
 */
export const getDisplayBathrooms = (property: Property): string => {
  // For land/lots, always show - for bed/bath
  const isLand = property.type === 'Land' || property.type === 'land' || 
               (typeof property.type === 'string' && property.type.toLowerCase().includes('land'));
  if (isLand) {
    return '-';
  }
  
  const unitsGroup = (property as any).rentalMetadata?.unitsGroup;
  
  if (!unitsGroup || !Array.isArray(unitsGroup) || unitsGroup.length === 0) {
    return property.details?.bathrooms && property.details.bathrooms > 0 ? `${property.details.bathrooms}` : '-';
  }
  
  if (unitsGroup.length === 1) {
    const unit = unitsGroup[0];
    const value = unit.bathrooms || property.details?.bathrooms || 0;
    return value && value > 0 ? `${value}` : '-';
  }
  
  // Multiple unit types - show range
  const values = unitsGroup
    .map(unit => unit.bathrooms || 0)
    .filter(val => val > 0);
  
  if (values.length === 0) {
    return property.details?.bathrooms && property.details.bathrooms > 0 ? `${property.details.bathrooms}` : '-';
  }
  
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  
  return minValue === maxValue ? `${minValue}` : `${minValue}-${maxValue}`;
};

/**
 * Get the best display square footage for a property
 */
export const getDisplaySquareFeet = (property: Property): string => {
  const unitsGroup = (property as any).rentalMetadata?.unitsGroup;
  
  if (!unitsGroup || !Array.isArray(unitsGroup) || unitsGroup.length === 0) {
    return property.details?.squareFeet && property.details.squareFeet > 0 ? `${property.details.squareFeet}` : '-';
  }
  
  if (unitsGroup.length === 1) {
    const unit = unitsGroup[0];
    const value = unit.squareFeet || property.details?.squareFeet || 0;
    return value && value > 0 ? `${value}` : '-';
  }
  
  // Multiple unit types - show range
  const values = unitsGroup
    .map(unit => unit.squareFeet || 0)
    .filter(val => val > 0);
  
  if (values.length === 0) {
    return property.details?.squareFeet && property.details.squareFeet > 0 ? `${property.details.squareFeet}` : '-';
  }
  
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  
  return minValue === maxValue ? `${minValue}` : `${minValue}-${maxValue}`;
};

/**
 * Search rental properties by location parameters
 */
export const searchRentPropertiesByLocation = debounceSearch(async (params: SearchParams): Promise<Property[]> => {
  try {
    const { city, state, zipCode, sort, ...otherParams } = params;
    
    let location = '';
    if (city && state) {
      location = `${city}, ${state}`;
    } else if (city) {
      location = city;
    } else if (state) {
      location = state;
    } else if (zipCode) {
      location = zipCode;
    } else {
      throw new Error('At least one location parameter is required');
    }
    
    let zillowSort: RentSearchParams['sort'] = 'relevance';
    if (sort === 'relevant') zillowSort = 'relevance';
    else if (sort === 'price_asc') zillowSort = 'price';
    else if (sort === 'price_desc') zillowSort = 'price';
    
    const searchParams: RentSearchParams = {
      location,
      status: 'forRent',
      sort: zillowSort,
      sortType: sort === 'price_asc' ? 'desc' : 'desc',
      ...otherParams
    };
    
    return await searchRentProperties(location, searchParams);
  } catch (error) {
    throw error;
  }
}, 200);
